import { format, isValid, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserAccessHistory } from '../../../api';
import { JdsTag } from '../../../components/jds/JdsTag';
import { Field } from './clienteUi';

export function getAccessStatusLabel(active) {
  return active ? 'Vigente' : 'Expirado';
}

const ACCESS_TYPE_BADGES = {
  block: {
    label: 'Bloqueio',
    color: 'var(--fg-error-default)',
    backgroundColor: 'var(--bg-error-subtler)',
  },
  release: {
    label: 'Liberação',
    color: 'var(--fg-success-strong)',
    backgroundColor: 'var(--bg-success-subtler)',
  },
};

const DEFAULT_ACCESS_TYPE_BADGE = {
  color: 'var(--fg-neutral-subtle)',
  backgroundColor: 'var(--bg-neutral-layer02)',
};

// Cor do JdsTag por tipo (getAccessTypeBadge segue exportado só pelo rótulo).
const ACCESS_TYPE_TAG_COLORS = { block: 'error', release: 'success' };

export function getAccessTypeBadge(type) {
  return ACCESS_TYPE_BADGES[type] ?? {
    ...DEFAULT_ACCESS_TYPE_BADGE,
    label: type,
  };
}

function formatTimestamp(dateString) {
  if (!dateString) {
    return '-';
  }

  let parsed;

  if (dateString.endsWith('Z')) {
    parsed = parseISO(dateString.replace('Z', ''));
  } else {
    parsed = parseISO(dateString);
  }

  if (!isValid(parsed)) {
    return '-';
  }

  return format(parsed, 'dd/MM/yyyy HH:mm');
}

export default function UserAccessHistory({ clienteId }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const userAccessStatus = useSelector(
    (state) => state.app.client_subscription?.userAccessStatus,
  );

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserAccessHistory(clienteId, { limit: 1 });
      const latestRecord = response.data?.data?.[0] ?? null;
      setRecord(latestRecord);
    } catch {
      setRecord(null);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, userAccessStatus]);

  if (loading && !hasLoaded) {
    return null;
  }

  if (!record) {
    return (
      <div className="jds-card mb-5">
        <h3 className="jds-title-sm">Controle de acesso manual</h3>
        <p className="text-muted mb-0 mt-2">
          Nenhuma liberação ou bloqueio manual registrado.
        </p>
      </div>
    );
  }

  const statusLabel = getAccessStatusLabel(record.active);
  const isVigente = record.active;
  const typeBadge = getAccessTypeBadge(record.type);

  return (
    <div className="jds-card mb-5">
      <h3 className="jds-title-sm">Status de liberação de acesso</h3>

      <div className="cd-fields-row">
        <Field label="Tipo">
          <JdsTag
            color={ACCESS_TYPE_TAG_COLORS[record.type] ?? 'neutral'}
            label={typeBadge.label}
          />
        </Field>
        <Field label="Operador">
          {record.operator
            ? `${record.operator.name} (${record.operator.email})`
            : '-'}
        </Field>
        <Field label="Status">
          <JdsTag
            color={isVigente ? 'success' : 'neutral'}
            label={statusLabel}
          />
        </Field>
      </div>

      <div className="cd-fields-row">
        <Field label="Data da ação">{formatTimestamp(record.timestamp)}</Field>
        <Field label="Data de validade">
          {formatTimestamp(record.expires_at)}
        </Field>
      </div>

      <Field label="Motivo">{record.reason || '-'}</Field>

      {record.motivation && (
        <Field label="Motivação">{record.motivation}</Field>
      )}
    </div>
  );
}
